import { useThemeUI, get } from 'theme-ui';
import { jsx, jsxs } from '@theme-ui/core/jsx-runtime';
import { getColor } from '@theme-ui/color';
import Color from 'color';
import { createElement } from '@theme-ui/core';

const useTheme = () => useThemeUI().theme;

function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}

function _objectWithoutPropertiesLoose(r, e) {
  if (null == r) return {};
  var t = {};
  for (var n in r) if ({}.hasOwnProperty.call(r, n)) {
    if (-1 !== e.indexOf(n)) continue;
    t[n] = r[n];
  }
  return t;
}

const Card = props => jsx("div", _extends({}, props, {
  sx: {
    variant: 'styles.Card'
  }
}));

const _excluded$3 = ["fontSize", "fontFamily", "lineHeight", "fontWeight", "children", "truncate"];
const TypeStyle = _ref => {
  let {
      fontSize = 5,
      fontFamily = 'body',
      lineHeight = 'body',
      fontWeight = 'body',
      children = 'Aa',
      truncate = true
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded$3);
  return jsx(Card, _extends({}, props, {
    children: children,
    sx: _extends({
      fontFamily,
      fontSize,
      lineHeight,
      fontWeight
    }, truncate ? {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    } : {})
  }));
};

const _excluded$2 = ["reverse"];
const getValue = fontSize => typeof fontSize === 'number' ? `${fontSize}px` : fontSize;
const TypeScale = _ref => {
  let {
      reverse = true
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded$2);
  const theme = useTheme() || {};
  const fontSizeEntries = reverse ? Object.entries(theme.fontSizes || []).reverse() : Object.entries(theme.fontSizes || []);
  return jsx("div", {
    sx: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'baseline'
    },
    children: fontSizeEntries.map(([key, val]) => {
      if (!val || typeof val === 'object') {
        // TODO: `theme.fontSizes` can be a deeply nested object.
        // We should either update the types or recursively render here.
        return null;
      }
      return jsx(TypeStyle, _extends({
        fontSize: key,
        sx: {
          mr: 3
        },
        children: getValue(val)
      }, props), key);
    })
  });
};

const HeadingStyle = props => jsx(TypeStyle, _extends({
  fontFamily: "heading",
  fontWeight: "heading",
  lineHeight: "heading"
}, props));

const FontFamily = ({
  name
}) => {
  const {
    fonts
  } = useTheme();
  return get(fonts, name);
};

const toHex = raw => {
  try {
    return Color(raw).hex();
  } catch (e) {
    return;
  }
};

const _excluded$1 = ["color", "name", "size", "label"];
const ColorSwatch = _ref => {
  let {
      color,
      name,
      size = 128,
      label = true
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded$1);
  const theme = useTheme();
  const value = getColor(theme, color);
  return jsxs("div", _extends({}, props, {
    title: `${toHex(value)}`,
    children: [jsx("div", {
      sx: {
        width: size,
        height: size,
        bg: color
      }
    }), label && jsx("div", {
      sx: {
        py: 2
      },
      children: name || color
    })]
  }));
};

const _excluded = ["colors", "name", "omit", "render", "size"],
  _excluded2 = ["omit", "mode"];
const join = (...args) => args.filter(Boolean).join('.');
const ColorRow = _ref => {
  let {
      colors,
      name,
      omit = ['modes'],
      render,
      size
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  return jsx("div", {
    children: jsx("div", {
      sx: {
        display: 'flex',
        flexWrap: 'wrap'
      },
      children: Object.keys(colors).map(key => {
        const color = colors[key];
        if (!color || omit.includes(key)) return false;
        const id = join(name, key);
        if (typeof color === 'object') {
          return createElement(ColorRow, _extends({}, props, {
            key: key,
            name: id,
            colors: color,
            size: size,
            omit: omit
          }));
        }
        const swatch = createElement(ColorSwatch, _extends({}, props, {
          key: key,
          name: id,
          color: id,
          size: size,
          sx: {
            m: 2
          }
        }));
        if (typeof render === 'function') {
          return render({
            swatch,
            color,
            key,
            name: id
          });
        }
        return swatch;
      })
    })
  });
};
const ColorPalette = _ref2 => {
  let {
      omit,
      mode
    } = _ref2,
    props = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const theme = useTheme();
  let colors = theme.colors;
  if (mode && colors.modes) {
    colors = colors.modes[mode] || colors;
  }
  return jsx("div", {
    style: {
      marginLeft: -8,
      marginRight: -8
    },
    children: jsx(ColorRow, _extends({}, props, {
      omit: omit,
      colors: colors
    }))
  });
};

const ThemeCard = props => {
  return jsxs(Card, _extends({}, props, {
    sx: {
      color: 'text',
      bg: 'background'
    },
    children: [jsx(TypeStyle, {}), jsx(ColorPalette, {
      label: false,
      size: 32
    })]
  }));
};

export { ColorPalette, ColorSwatch, FontFamily, HeadingStyle, ThemeCard, TypeScale, TypeStyle, useTheme };
